import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Factory, Truck, Scale, Plus, Trash2, RefreshCw, Calendar, Lock, UserCircle, History, Search, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useProductCodes } from '../context/ProductCodesContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { key: 'inward_grn',       label: 'Inward / GRN',        icon: Truck },
  { key: 'outward_dispatch', label: 'Outward / Dispatch',  icon: Truck },
  { key: 'production_room',  label: 'Production Room',     icon: Factory },
  { key: 'batch_weighing',   label: 'Batch Weighing',      icon: Scale },
];

const STATUS_OPTS = ['completed', 'in_progress', 'hold'];

const todayStr = () => new Date().toISOString().slice(0, 10);

const Input = ({ value, onChange, type = 'text', placeholder, testid, disabled }) => (
  <input
    data-testid={testid}
    type={type}
    value={value === null || value === undefined ? '' : value}
    disabled={disabled}
    onChange={e => onChange(type === 'number' ? (e.target.value === '' ? null : parseFloat(e.target.value)) : e.target.value)}
    placeholder={placeholder}
    className="min-w-[100px] w-full bg-background border border-border rounded px-2 py-1.5 text-[11px] text-foreground focus:outline-none focus:border-orange-500 disabled:opacity-60"
  />
);

const Select = ({ value, options, onChange, testid, disabled }) => (
  <select
    data-testid={testid}
    value={value || ''}
    disabled={disabled}
    onChange={e => onChange(e.target.value)}
    className="min-w-[120px] w-full bg-background border border-border rounded px-2 py-1.5 text-[11px] text-foreground disabled:opacity-60"
  >
    {options.map(o => (typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </select>
);

export default function ProductionTasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'manager';
  const [tab, setTab] = useState('inward_grn');
  const [users, setUsers] = useState([]);
  const [dash, setDash] = useState(null);
  const [viewDate, setViewDate] = useState(todayStr());
  const [filterUser, setFilterUser] = useState('');

  const isToday = viewDate === todayStr();
  const canEdit = isAdmin || isToday;
  const [sending, setSending] = useState(false);

  const sendDailyEmail = async () => {
    if (!window.confirm(`Send the 3-table production digest for ${viewDate}${filterUser ? ' (user: ' + filterUser + ')' : ''} to the designated recipients?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/production-tasks/send-daily-email`, {
        date: viewDate,
        user_name: filterUser || undefined,
      }, { withCredentials: true });
      if (res.data?.email_status?.sent) {
        toast.success(`Digest emailed to ${res.data.recipients.length} recipient(s)`);
      } else {
        toast.info(res.data?.email_status?.reason || 'Digest saved (email not configured)');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const load = async () => {
    try {
      const [dRes, uRes] = await Promise.all([
        axios.get(`${API}/production-tasks/dashboard`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
      ]);
      setDash(dRes.data);
      setUsers(uRes.data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { load(); }, []);

  // Distinct users with activity (from dedicated endpoint)
  const [activeUsers, setActiveUsers] = useState([]);
  useEffect(() => {
    axios.get(`${API}/production-tasks/users-active`, { withCredentials: true })
      .then(r => setActiveUsers(r.data?.users || []))
      .catch(() => setActiveUsers([]));
  }, [viewDate]);

  return (
    <div data-testid="production-tasks-page" className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Factory className="text-orange-400" size={24} /> Production Tasks
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Shop-floor daily log: inward/outward dispatch, production activity, batch weighing.</p>
        </div>
        <button onClick={load} data-testid="pt-refresh" className="p-2 rounded-lg bg-secondary hover:bg-accent text-foreground border border-border"><RefreshCw size={14} /></button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {CATEGORIES.map(c => {
          const Icon = c.icon;
          const count = dash?.counts?.[c.key] || 0;
          return (
            <div key={c.key} data-testid={`pt-kpi-${c.key}`} className="bg-card border border-border rounded-xl p-3">
              <div className="text-[10px] text-muted-foreground uppercase flex items-center gap-1.5"><Icon size={12} /> {c.label}</div>
              <div className="text-xl font-bold text-foreground mt-1">{count} <span className="text-[10px] text-muted-foreground font-normal">today</span></div>
            </div>
          );
        })}
      </div>

      {/* User scroller · shown to everyone */}
      {activeUsers.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-3">
          <div className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <UserCircle size={12} /> Users feeding entries · tap to filter
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => setFilterUser('')} data-testid="pt-user-filter-all"
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border ${filterUser === '' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-foreground hover:bg-accent'}`}>All users</button>
            {activeUsers.map(u => (
              <button key={u.user_name} onClick={() => setFilterUser(u.user_name)}
                data-testid={`pt-user-filter-${u.user_name}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border flex items-center gap-1.5 ${filterUser === u.user_name ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-foreground hover:bg-accent'}`}>
                <UserCircle size={11} />{u.user_name}
                <span className="text-[10px] opacity-70">· {u.total}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Date / lock bar */}
      <div className="bg-card border border-border rounded-xl p-3 flex items-center gap-3 flex-wrap justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar size={13} /> Date:
            <input type="date" value={viewDate} onChange={e => setViewDate(e.target.value)} data-testid="pt-view-date"
              className="bg-background border border-border rounded px-2 py-1 text-xs text-foreground" />
          </label>
          {!isToday && (
            <span className="flex items-center gap-1 text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
              <Lock size={10} /> read-only{isAdmin ? ' (admin/manager can edit)' : ''}
            </span>
          )}
          {filterUser && (
            <span className="text-[11px] bg-sky-500/10 text-sky-300 border border-sky-500/30 rounded-full px-2 py-0.5">
              User: {filterUser}
            </span>
          )}
        </div>
        {isAdmin && (
          <button onClick={sendDailyEmail} disabled={sending} data-testid="pt-send-email"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:opacity-90 font-semibold disabled:opacity-50">
            {sending ? <RefreshCw size={12} className="animate-spin" /> : <Mail size={12} />}
            Email 3-table digest
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map(c => {
          const Icon = c.icon;
          return (
            <button
              key={c.key} data-testid={`pt-tab-${c.key}`} onClick={() => setTab(c.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 ${
                tab === c.key ? 'bg-orange-600 border-orange-500 text-white' : 'bg-secondary border-border text-foreground hover:bg-accent'
              }`}
            ><Icon size={13} /> {c.label}</button>
          );
        })}
      </div>

      <TaskSheet category={tab} users={users} onChange={load} viewDate={viewDate} filterUser={filterUser} canEdit={canEdit} isAdmin={isAdmin} />
    </div>
  );
}

function TaskSheet({ category, users, onChange, viewDate, filterUser, canEdit, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState(emptyRow());

  function emptyRow() {
    return {
      category, user_name: '', task_date: viewDate, task_time: '',
      line: '', product_name: '', batch_ref: '', client_name: '',
      quantity: null, unit: 'kg', num_batches: null, activity: '',
      ref_no: '', vehicle_no: '', vendor_or_client: '',
      status: 'completed', notes: '',
    };
  }

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/production-tasks/`, { params: { category, days: 60 }, withCredentials: true });
      let items = res.data?.items || [];
      if (viewDate) items = items.filter(r => r.task_date === viewDate);
      if (filterUser) items = items.filter(r => r.user_name === filterUser);
      setRows(items);
    } finally { setLoading(false); }
  };

  useEffect(() => { setNewRow(emptyRow()); load(); /* eslint-disable-next-line */ }, [category, viewDate, filterUser]);

  const addRow = async () => {
    try {
      await axios.post(`${API}/production-tasks/`, { ...newRow, task_date: viewDate }, { withCredentials: true });
      toast.success('Task logged'); setNewRow(emptyRow()); load(); onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add failed'); }
  };

  const updateRow = async (row, patch) => {
    try {
      await axios.patch(`${API}/production-tasks/${row.id}`, patch, { withCredentials: true });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...patch } : r));
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm('Delete entry?')) return;
    try { await axios.delete(`${API}/production-tasks/${row.id}`, { withCredentials: true }); load(); onChange?.(); }
    catch { toast.error('Delete denied (admin/manager only)'); }
  };

  const userOpts = useMemo(() => [{ value: '', label: '— User —' }, ...users.map(u => ({ value: u.name, label: u.name }))], [users]);

  // Pull planned batches + pending sales orders for the batch-weighing linked-to picker
  const [plannedBatches, setPlannedBatches] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  useEffect(() => {
    if (category !== 'batch_weighing') return;
    axios.get(`${API}/production/batches`, { withCredentials: true })
      .then(r => setPlannedBatches((r.data || []).filter(b => b.status === 'Planned').slice(0, 60)))
      .catch(() => {});
    axios.get(`${API}/sales-pipeline/orders`, { params: { status: 'Accepted' }, withCredentials: true })
      .then(r => setPendingOrders((r.data || []).slice(0, 60)))
      .catch(() => {});
  }, [category]);

  if (category === 'inward_grn') {
    return (
      <Sheet rows={rows} loading={loading} newRow={newRow} setNewRow={setNewRow} addRow={addRow} updateRow={updateRow} deleteRow={deleteRow} userOpts={userOpts} category={category} canEdit={canEdit} isAdmin={isAdmin}
        columns={[
          { key: 'activity',    label: 'Activity',    input: 'select', options: ['', 'GRN Receipt', 'Vendor Return', 'Internal Transfer In', 'Sample Receive'] },
          { key: 'user_name',   label: 'Received By', input: 'userSelect' },
          { key: 'task_time',   label: 'Time',        input: 'time' },
          { key: 'ref_no',      label: 'GRN #',       input: 'text', placeholder: 'GRN-0001' },
          { key: 'vendor_or_client', label: 'Vendor',  input: 'text' },
          { key: 'product_name',label: 'Material',    input: 'productName' },
          { key: 'quantity',    label: 'Qty',         input: 'number' },
          { key: 'unit',        label: 'Unit',        input: 'select', options: ['kg', 'packs', 'litres', 'boxes', 'tons'] },
          { key: 'vehicle_no',  label: 'Vehicle #',   input: 'text' },
          { key: 'notes',       label: 'Notes',       input: 'text' },
        ]}
      />
    );
  }
  if (category === 'outward_dispatch') {
    return (
      <Sheet rows={rows} loading={loading} newRow={newRow} setNewRow={setNewRow} addRow={addRow} updateRow={updateRow} deleteRow={deleteRow} userOpts={userOpts} category={category} canEdit={canEdit} isAdmin={isAdmin}
        columns={[
          { key: 'activity',    label: 'Activity',    input: 'select', options: ['', 'Client Dispatch', 'Courier', 'Sample Send', 'Vendor Return Out', 'Internal Transfer Out'] },
          { key: 'user_name',   label: 'Dispatched By', input: 'userSelect' },
          { key: 'task_time',   label: 'Time',        input: 'time' },
          { key: 'ref_no',      label: 'Dispatch #',  input: 'text', placeholder: 'DSP-0001' },
          { key: 'vendor_or_client', label: 'Client', input: 'text' },
          { key: 'product_name',label: 'Product',     input: 'productName' },
          { key: 'quantity',    label: 'Qty',         input: 'number' },
          { key: 'unit',        label: 'Unit',        input: 'select', options: ['kg', 'packs', 'litres', 'boxes', 'tons'] },
          { key: 'vehicle_no',  label: 'Vehicle #',   input: 'text' },
          { key: 'notes',       label: 'Notes',       input: 'text' },
        ]}
      />
    );
  }
  if (category === 'production_room') {
    return (
      <Sheet rows={rows} loading={loading} newRow={newRow} setNewRow={setNewRow} addRow={addRow} updateRow={updateRow} deleteRow={deleteRow} userOpts={userOpts} category={category} canEdit={canEdit} isAdmin={isAdmin}
        columns={[
          { key: 'activity',     label: 'Activity',   input: 'select', options: ['', 'Setup', 'Cleaning', 'Mixing', 'Grinding', 'Shifter', 'Packing', 'Other'] },
          { key: 'user_name',    label: 'By',         input: 'userSelect' },
          { key: 'task_time',    label: 'Time',       input: 'time' },
          { key: 'line',         label: 'Line',       input: 'select', options: ['', 'PL1', 'PL2', 'PL3'] },
          { key: 'product_name', label: 'Product',    input: 'productName' },
          { key: 'batch_ref',    label: 'Batch Ref',  input: 'text' },
          { key: 'quantity',     label: 'Output Qty', input: 'number' },
          { key: 'unit',         label: 'Unit',       input: 'select', options: ['kg', 'packs', 'litres'] },
          { key: 'status',       label: 'Status',     input: 'select', options: STATUS_OPTS },
          { key: 'notes',        label: 'Notes',      input: 'text' },
        ]}
      />
    );
  }
  // batch_weighing — with "Linked to" picker (general / planned batch / sales order)
  const batchOpts = [
    { value: '', label: '— General (not linked) —' },
    ...plannedBatches.map(b => ({ value: `b:${b.id}`, label: `Batch · ${b.id} · ${b.product_name}` })),
    ...pendingOrders.map(o => ({ value: `o:${o.id}`, label: `Order · ${o.voucher_no || o.id} · ${o.client}` })),
  ];
  return (
    <Sheet rows={rows} loading={loading} newRow={newRow} setNewRow={setNewRow} addRow={addRow} updateRow={updateRow} deleteRow={deleteRow} userOpts={userOpts} category={category} canEdit={canEdit} isAdmin={isAdmin}
      linkedOpts={batchOpts}
      columns={[
        { key: 'user_name',    label: 'Weighed By',  input: 'userSelect' },
        { key: 'task_time',    label: 'Time',        input: 'time' },
        { key: 'product_name', label: 'Product',     input: 'productName' },
        { key: 'num_batches',  label: '# Batches',   input: 'number' },
        { key: 'quantity',     label: 'Total Qty',   input: 'number' },
        { key: 'unit',         label: 'Unit',        input: 'select', options: ['kg', 'packs', 'litres'] },
        { key: '_linked',      label: 'Linked to',   input: 'linkedSelect' },
        { key: 'line',         label: 'Line',        input: 'select', options: ['', 'PL1', 'PL2', 'PL3'] },
        { key: 'status',       label: 'Done?',       input: 'doneChip' },
        { key: 'notes',        label: 'Notes',       input: 'text' },
      ]}
    />
  );
}

function Sheet({ rows, loading, newRow, setNewRow, addRow, updateRow, deleteRow, columns, userOpts, linkedOpts, category, canEdit, isAdmin }) {
  // Virtual field `_linked` combines linked_batch_id + linked_order_id into
  // a single "b:<id>" / "o:<id>" selection string for the UI.
  const readLinked = (row) => row.linked_batch_id ? `b:${row.linked_batch_id}` : row.linked_order_id ? `o:${row.linked_order_id}` : '';
  const applyLinked = (patch, val) => {
    if (val && val.startsWith('b:')) { patch.linked_batch_id = val.slice(2); patch.linked_order_id = ''; }
    else if (val && val.startsWith('o:')) { patch.linked_order_id = val.slice(2); patch.linked_batch_id = ''; }
    else { patch.linked_batch_id = ''; patch.linked_order_id = ''; }
  };

  const renderCell = (col, row, onChange, disabled) => {
    if (col.input === 'linkedSelect') {
      return <Select value={readLinked(row)} options={linkedOpts || []} disabled={disabled}
        onChange={v => {
          const patch = {};
          applyLinked(patch, v);
          onChange(patch);      // Sheet will spread
        }} />;
    }
    const v = row[col.key];
    if (col.input === 'doneChip') {
      const done = v === 'completed';
      return (
        <button type="button"
          data-testid={`pt-done-chip-${row.id || 'new'}`}
          disabled={disabled}
          onClick={() => onChange(done ? 'in_progress' : 'completed')}
          className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all whitespace-nowrap ${done ? 'bg-green-600 text-white border-green-500 hover:bg-green-700' : 'bg-secondary text-muted-foreground border-border hover:bg-accent'}`}>
          {done ? '✓ Yes' : '○ No'}
        </button>
      );
    }
    if (col.input === 'userSelect') return <Select value={v} options={userOpts} onChange={onChange} disabled={disabled} />;
    if (col.input === 'select') return <Select value={v} options={col.options} onChange={onChange} disabled={disabled} />;
    if (col.input === 'date') return <Input value={v} type="date" onChange={onChange} disabled={disabled} />;
    if (col.input === 'time') return <Input value={v} placeholder="HH:MM" onChange={onChange} disabled={disabled} />;
    if (col.input === 'number') return <Input value={v} type="number" onChange={onChange} disabled={disabled} />;
    if (col.input === 'productName') {
      return (
        <div className="flex items-center gap-1.5 min-w-[180px]">
          <Input value={v || ''} onChange={onChange} placeholder={col.placeholder || 'Product'} disabled={disabled} />
          {isAdmin && v && <ProductCodeHint name={v} />}
        </div>
      );
    }
    return <Input value={v || ''} onChange={onChange} placeholder={col.placeholder} disabled={disabled} />;
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid={`pt-sheet-${category}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse" style={{ minWidth: 1200 }}>
          <thead className="bg-secondary/60 text-muted-foreground uppercase text-[10px]">
            <tr>
              {columns.map(c => <th key={c.key} className="px-3 py-2 text-left whitespace-nowrap">{c.label}</th>)}
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {canEdit && (
              <tr className="bg-orange-900/20 border-t border-orange-700/40" data-testid={`pt-new-row-${category}`}>
                {columns.map(c => (
                  <td key={c.key} className="px-2 py-1.5">
                    {renderCell(c, newRow, v => {
                      // linkedSelect passes an object patch; others pass a scalar
                      if (c.input === 'linkedSelect') setNewRow({ ...newRow, ...v });
                      else setNewRow({ ...newRow, [c.key]: v });
                    }, false)}
                  </td>
                ))}
                <td className="px-2 py-1.5">
                  <button data-testid={`pt-add-${category}`} onClick={addRow} className="p-1.5 rounded-md bg-orange-600 hover:bg-orange-500 text-white">
                    <Plus size={12} />
                  </button>
                </td>
              </tr>
            )}
            {loading ? <tr><td colSpan={columns.length + 1} className="p-6 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={columns.length + 1} className="p-6 text-center text-muted-foreground">{canEdit ? 'No entries yet. Add the first row above.' : 'No entries on this date.'}</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} data-testid={`pt-row-${r.id}`} className="border-t border-border hover:bg-accent/40">
                    {columns.map(c => (
                      <td key={c.key} className="px-2 py-1.5">
                        {renderCell(c, r, v => {
                          if (c.input === 'linkedSelect') updateRow(r, v);
                          else updateRow(r, { [c.key]: v });
                        }, !canEdit)}
                      </td>
                    ))}
                    <td className="px-2 py-1.5">
                      {isAdmin && <button data-testid={`pt-del-${r.id}`} onClick={() => deleteRow(r)} className="p-1.5 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-900/30"><Trash2 size={11} /></button>}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Tiny passive chip shown inside ProductionTasks product_name cells — looks
// up the typed text in the ProductCodesContext and renders a monospace
// code pill when a match exists.
function ProductCodeHint({ name }) {
  const { lookup } = useProductCodes();
  const code = lookup(name);
  if (!code) return null;
  return (
    <span
      data-testid="pt-code-hint"
      title={`Product code: ${code}`}
      className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-mono text-[9px] font-semibold border border-amber-600/30 whitespace-nowrap">
      {code}
    </span>
  );
}
